// One dialog for every support action: environment strip, what it does, the
// runner, a required reason, and — for ban / delete — the email typed back.

import { ReasonHint } from "./ReasonHint";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { MIN_REASON_LENGTH, type AdminRunnerDetail, type SupportActionRequest, type SupportActionResult } from "@paceroo/domain/views/admin";
import { AdminApiError, adminPost } from "../lib/api";
import { useShell } from "../shell/context";
import s from "./ActionDialog.module.css";
import type { ActionSpec } from "./supportActions";

export function ActionDialog({
  spec,
  detail,
  preselect,
  onCancel,
  onDone,
}: {
  spec: ActionSpec;
  detail: AdminRunnerDetail;
  /** Pre-chosen assistant or plan (e.g. the Disconnect button on one assistant). */
  preselect?: string | null;
  onCancel: () => void;
  onDone: (result: SupportActionResult) => void;
}) {
  const { me, bp } = useShell();
  const r = detail.runner;
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [choice, setChoice] = useState<string>(preselect ?? spec.pick?.options[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const mobile = bp === "mobile";
  const prod = me.environment === "prod";

  useEffect(() => {
    reasonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  const typedOk = typed.trim().toLowerCase() === r.email.toLowerCase();
  const blocked = busy || reason.trim().length < MIN_REASON_LENGTH || (spec.destructive && !typedOk) || (spec.pick !== undefined && !choice);

  const submit = async () => {
    if (blocked) return;
    setBusy(true);
    setError(null);
    const body: SupportActionRequest = { reason: reason.trim() };
    if (spec.destructive) body.confirmEmail = typed.trim();
    if (spec.pick?.kind === "assistant") body.clientId = choice;
    if (spec.pick?.kind === "plan") body.planId = choice;
    try {
      onDone(await adminPost<SupportActionResult>(`runners/${encodeURIComponent(r.id)}/actions/${spec.action}`, body));
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className={clsx(s.scrim, mobile && s.scrimSheet)} onClick={() => !busy && onCancel()}>
      <div role="dialog" aria-modal="true" aria-labelledby="action-title" className={clsx(s.dialog, mobile && s.sheet)} onClick={(e) => e.stopPropagation()}>
        <div className={clsx(s.env, prod ? s.envProd : s.envDev)}>
          <span className={s.envBullet} />
          {prod ? "Production · this changes a real runner’s account" : `${me.environment === "local" ? "Local" : "Dev"} · ${window.location.host} · test data only`}
        </div>
        <div className={s.body}>
          <span className="kicker ink-mute">Support action · {r.name}</span>
          <h2 id="action-title" className={clsx("t-serif-md", s.title)}>
            {spec.label}
          </h2>
          <p className={clsx("t-body-md ink-soft", s.what)}>{spec.what}</p>
          <div className={s.runner}>
            <span className="kicker ink-mute">Runner</span>
            <span className="t-mono-sm">{r.email}</span>
            <span className="t-mono-xs ink-mute">{r.id}</span>
          </div>
          {spec.pick && (
            <label className={s.field}>
              <span className="t-label">{spec.pick.kind === "assistant" ? "Assistant" : "Plan"}</span>
              <select className={clsx(s.control, s.select)} value={choice} onChange={(e) => setChoice(e.target.value)}>
                {spec.pick.options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className={s.field}>
            <span className="t-label">
              Reason <span className={clsx("ink-mute", s.hint)}>· required, saved to the audit log</span>
            </span>
            <textarea
              ref={reasonRef}
              className={clsx(s.control, s.textarea)}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What happened, and how you confirmed it"
            />
            <ReasonHint reason={reason} />
          </label>
          {spec.destructive && (
            <label className={s.field}>
              <span className="t-label">
                Type{" "}
                <span className="t-mono-sm" style={{ color: "var(--alert)" }}>
                  {r.email}
                </span>{" "}
                to confirm
              </span>
              <input
                className={clsx(s.control, s.typed, typed && !typedOk && s.typedBad)}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                spellCheck={false}
                autoComplete="off"
              />
            </label>
          )}
          {error && <p className={clsx("t-body-sm", s.error)}>{error}</p>}
        </div>
        <div className={clsx(s.buttons, mobile && s.buttonsStacked)}>
          <button className="btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          {spec.destructive ? (
            <button className={s.danger} onClick={submit} disabled={blocked}>
              {busy ? "Working…" : spec.btn}
            </button>
          ) : (
            <button className="btn-primary" onClick={submit} disabled={blocked}>
              {busy ? "Working…" : spec.btn}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
